import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Video,
  Newspaper,
  Images,
  Mail,
  Users,
  Megaphone,
  BellRing,
  Handshake,
  BarChart3,
  UserCog,
  Settings,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { FEATURES } from '@/lib/features';
import { sidebarAllowed } from '@/lib/rbac';

interface SidebarItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
}

const sidebarItems: SidebarItem[] = [
  { to: '/admin/dashboard', labelKey: 'admin.nav.dashboard', icon: LayoutDashboard },
  { to: '/admin/videos', labelKey: 'admin.nav.videos', icon: Video },
  { to: '/admin/blog', labelKey: 'admin.nav.blog', icon: Newspaper },
  { to: '/admin/gallery', labelKey: 'admin.nav.gallery', icon: Images },
  { to: '/admin/contact', labelKey: 'admin.nav.contact', icon: Mail },
  { to: '/admin/family', labelKey: 'admin.nav.family', icon: Users },
  { to: '/admin/announcements', labelKey: 'admin.nav.announcements', icon: Megaphone },
  {
    to: '/admin/notification-banners',
    labelKey: 'admin.nav.notificationBanners',
    icon: BellRing,
  },
  ...(FEATURES.sponsors
    ? [
        {
          to: '/admin/sponsors',
          labelKey: 'admin.nav.sponsors',
          icon: Handshake,
        } satisfies SidebarItem,
      ]
    : []),
  { to: '/admin/analytics', labelKey: 'admin.nav.analytics', icon: BarChart3 },
  { to: '/admin/users', labelKey: 'admin.nav.users', icon: UserCog },
  { to: '/admin/settings', labelKey: 'admin.nav.settings', icon: Settings },
];

interface AdminSidebarProps {
  open?: boolean;
  collapsed?: boolean;
  onClose?: () => void;
  onToggleCollapse?: () => void;
}

export default function AdminSidebar({
  open = false,
  collapsed = true,
  onClose,
  onToggleCollapse,
}: AdminSidebarProps) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { isDesktop, isTablet, isMobile } = useBreakpoint();
  const visibleItems = sidebarItems.filter((item) =>
    sidebarAllowed(role, item.to),
  );

  useEffect(() => {
    if (!isMobile || !open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, open]);

  const showLabels = isDesktop || (isMobile && open) || (isTablet && !collapsed);
  const tabletCollapsed = isTablet && collapsed;

  return (
    <>
      <div
        className={[
          'fixed inset-0 z-40 bg-black/55 backdrop-blur-sm transition-opacity lg:hidden',
          open || (isTablet && !collapsed)
            ? 'opacity-100'
            : 'pointer-events-none opacity-0',
        ].join(' ')}
        onClick={onClose}
        aria-hidden={!(open || (isTablet && !collapsed))}
      />

      <aside
        id="admin-sidebar"
        aria-label={t('admin.menu')}
        className={[
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-white/10 bg-primary-bg/95 backdrop-blur-xl transition-all duration-300',
          isMobile
            ? open
              ? 'w-72 translate-x-0'
              : 'w-72 -translate-x-full'
            : '',
          isTablet
            ? tabletCollapsed
              ? 'w-20 translate-x-0'
              : 'w-72 translate-x-0 shadow-2xl'
            : '',
          isDesktop ? 'w-72 translate-x-0' : '',
          !isMobile && !isTablet && !isDesktop ? 'w-72 -translate-x-full' : '',
        ].join(' ')}
      >
        <div
          className={[
            'flex min-h-20 items-center border-b border-white/10',
            showLabels ? 'justify-between gap-2 px-4 py-3 sm:px-5' : 'justify-center px-2 py-3',
          ].join(' ')}
        >
          <NavLink
            to="/admin/dashboard"
            onClick={onClose}
            className={showLabels ? 'min-w-0 flex-1 leading-tight' : 'sr-only'}
          >
            <span className="block break-words font-display text-lg font-semibold tracking-tight sm:text-xl">
              <span className="text-youtube-red">Lombok</span>
              <span className="text-white">-Japan </span>
              <span className="text-gold">Family</span>
            </span>
          </NavLink>

          {!showLabels && (
            <span
              className="flex h-12 w-12 items-center justify-center rounded-2xl bg-youtube-red/15 text-base font-bold text-youtube-red"
              aria-hidden
            >
              LJ
            </span>
          )}

          <div className="flex shrink-0 items-center gap-1">
            {isTablet && onToggleCollapse ? (
              <button
                type="button"
                className="touch-target inline-flex items-center justify-center rounded-xl border border-white/10 p-2 text-muted transition-colors hover:text-white"
                onClick={onToggleCollapse}
                aria-label={
                  collapsed ? t('admin.expandSidebar') : t('admin.collapseSidebar')
                }
              >
                {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
              </button>
            ) : null}
            <button
              type="button"
              className="touch-target inline-flex items-center justify-center rounded-xl border border-white/10 p-2 text-muted transition-colors hover:text-white lg:hidden"
              onClick={onClose}
              aria-label={t('admin.closeMenu')}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <nav className="scrollbar-thin flex-1 space-y-1 overflow-y-auto px-2 py-4 md:px-3">
          {visibleItems.map(({ to, labelKey, icon: Icon }) => {
            const label = t(labelKey);
            return (
              <NavLink
                key={to}
                to={to}
                onClick={onClose}
                title={label}
                className={({ isActive }) =>
                  [
                    'group touch-target flex items-center gap-3 rounded-2xl text-sm transition-all duration-300',
                    showLabels ? 'px-3.5 py-3' : 'justify-center px-2 py-3',
                    isActive
                      ? 'bg-gradient-to-r from-youtube-red/25 to-youtube-red/5 text-white shadow-[0_10px_30px_rgba(220,38,38,0.18)] ring-1 ring-youtube-red/40'
                      : 'text-muted hover:bg-white/5 hover:text-white',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={[
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
                        isActive
                          ? 'bg-youtube-red/20 text-youtube-red'
                          : 'bg-white/5 text-muted group-hover:text-white',
                      ].join(' ')}
                    >
                      <Icon size={18} strokeWidth={1.8} aria-hidden />
                    </span>
                    {showLabels ? (
                      <span className="font-medium">{label}</span>
                    ) : (
                      <span className="sr-only">{label}</span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div
          className={[
            'border-t border-white/10 py-4',
            showLabels ? 'px-5' : 'px-2 text-center',
          ].join(' ')}
        >
          {showLabels ? (
            <>
              <p className="text-xs text-muted">{t('admin.signedInAs')}</p>
              <p className="mt-1 text-sm font-medium uppercase text-white">
                {role ?? '—'}
              </p>
            </>
          ) : (
            <p className="text-[10px] uppercase tracking-wide text-gold">
              {role?.slice(0, 3) ?? '—'}
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
